/**
 * Platform Limits Constants
 *
 * Defines character limits, carousel/media limits, and video duration limits
 * for each supported social media platform.
 *
 * Based on official platform documentation as of May 2026.
 *
 * Character Counting:
 * - Different platforms count characters differently (especially emojis)
 * - Twitter/X counts emojis as 2 characters each
 * - Most other platforms count grapheme clusters (1 emoji = 1 character)
 * - Use checkLimitsWithCounting() for accurate platform-specific counting
 *
 * Usage:
 * ```tsx
 * import { PLATFORM_LIMITS, getPlatformLimit, checkLimitsWithCounting } from '@/constants/platformLimits';
 *
 * const limit = PLATFORM_LIMITS.linkedin.characters; // 3000
 * const result = checkLimitsWithCounting("Hello face", ['linkedin', 'twitter'], 'characters');
 * // Twitter will show higher count due to emoji weighting
 * ```
 */

import { countCharactersForPlatform } from '@/utils/characterCount';
import { timeSync } from '@/utils/perfTelemetry';

/**
 * Platform limit configuration
 */
export interface PlatformLimitConfig {
  /** Maximum character count for post caption */
  characters: number;
  /** Maximum number of images in a carousel post */
  carouselImages: number;
  /** Maximum video duration in seconds */
  videoMaxSeconds: number;
  /** Display name for UI */
  displayName: string;
}

/**
 * All supported platform limits
 * Keys match the platform type from the backend Channel model
 */
export const PLATFORM_LIMITS: Record<string, PlatformLimitConfig> = {
  linkedin: {
    characters: 3000,
    carouselImages: 20,
    videoMaxSeconds: 600, // 10 minutes
    displayName: 'LinkedIn',
  },
  twitter: {
    characters: 280,
    carouselImages: 4,
    videoMaxSeconds: 140, // 2 min 20 sec
    displayName: 'Twitter/X',
  },
  instagram: {
    characters: 2200,
    carouselImages: 10,
    videoMaxSeconds: 900, // 15 minutes
    displayName: 'Instagram',
  },
  facebook: {
    characters: 63206,
    carouselImages: 10,
    videoMaxSeconds: 1200, // 20 minutes
    displayName: 'Facebook',
  },
  tiktok: {
    characters: 2200,
    carouselImages: 35,
    videoMaxSeconds: 600, // 10 minutes
    displayName: 'TikTok',
  },
  youtube: {
    characters: 5000, // Description limit
    carouselImages: 0, // No carousel support
    videoMaxSeconds: 43200, // 12 hours
    displayName: 'YouTube',
  },
  pinterest: {
    characters: 500,
    carouselImages: 1,
    videoMaxSeconds: 120,
    displayName: 'Pinterest',
  },
  threads: {
    characters: 500,
    carouselImages: 20,
    videoMaxSeconds: 300, // 5 minutes
    displayName: 'Threads',
  },
  bluesky: {
    characters: 300,
    carouselImages: 4,
    videoMaxSeconds: 180,
    displayName: 'Bluesky',
  },
  google_my_business: {
    characters: 1500,
    carouselImages: 1,
    videoMaxSeconds: 30,
    displayName: 'Google Business',
  },
};

/**
 * Platform type derived from the limits object
 */
export type PlatformType = keyof typeof PLATFORM_LIMITS;

/**
 * Limit type for accessor functions
 */
export type LimitType = 'characters' | 'carouselImages' | 'videoMaxSeconds';

/**
 * Get a specific limit for a platform
 *
 * @param platform - Platform key (e.g., 'linkedin', 'twitter')
 * @param limitType - Type of limit to retrieve
 * @returns The limit value or a sensible default
 *
 * @example
 * getPlatformLimit('twitter', 'characters') // 280
 */
export function getPlatformLimit(platform: string, limitType: LimitType): number {
  const normalizedPlatform = platform.toLowerCase();
  const config = PLATFORM_LIMITS[normalizedPlatform];

  if (!config) {
    // Default fallbacks for unknown platforms
    const defaults: Record<LimitType, number> = {
      characters: 3000,
      carouselImages: 10,
      videoMaxSeconds: 300,
    };
    return defaults[limitType];
  }

  return config[limitType];
}

/**
 * Get the display name for a platform
 *
 * @param platform - Platform key
 * @returns Display name or capitalized key
 */
export function getPlatformDisplayName(platform: string): string {
  const normalizedPlatform = platform.toLowerCase();
  return PLATFORM_LIMITS[normalizedPlatform]?.displayName || platform.charAt(0).toUpperCase() + platform.slice(1);
}

/**
 * Get the most restrictive limit from a list of platforms
 *
 * @param platforms - Array of platform keys
 * @param limitType - Type of limit to check
 * @returns The lowest limit among all platforms, or default if no platforms
 *
 * @example
 * getMostRestrictiveLimit(['linkedin', 'twitter'], 'characters') // 280 (Twitter's limit)
 */
export function getMostRestrictiveLimit(platforms: string[], limitType: LimitType): number {
  if (platforms.length === 0) {
    return getPlatformLimit('linkedin', limitType); // Default to LinkedIn
  }

  const limits = platforms.map((p) => getPlatformLimit(p, limitType));
  return Math.min(...limits);
}

/**
 * Get the platform with the most restrictive limit
 *
 * @param platforms - Array of platform keys
 * @param limitType - Type of limit to check
 * @returns Platform key with the lowest limit
 */
export function getMostRestrictivePlatform(platforms: string[], limitType: LimitType): string {
  if (platforms.length === 0) {
    return 'linkedin';
  }

  let mostRestrictive = platforms[0];
  let lowestLimit = getPlatformLimit(platforms[0], limitType);

  for (const platform of platforms) {
    const limit = getPlatformLimit(platform, limitType);
    if (limit < lowestLimit) {
      lowestLimit = limit;
      mostRestrictive = platform;
    }
  }

  return mostRestrictive;
}

/**
 * Result of checking limits across platforms
 */
export interface LimitCheckResult {
  /** Whether the value is within all platform limits */
  isWithinAll: boolean;
  /** Platforms where value is within limit */
  withinLimits: Array<{ platform: string; limit: number; displayName: string }>;
  /** Platforms where value exceeds limit */
  overLimits: Array<{ platform: string; limit: number; displayName: string; overBy: number }>;
  /** The most restrictive limit among selected platforms */
  mostRestrictiveLimit: number;
  /** The platform with the most restrictive limit */
  mostRestrictivePlatform: string;
}

/**
 * Check a value against all selected platform limits
 *
 * @param value - Current value (e.g., character count, image count)
 * @param platforms - Array of platform keys
 * @param limitType - Type of limit to check
 * @returns Detailed check result with per-platform breakdown
 *
 * @example
 * const result = checkLimits(300, ['linkedin', 'twitter'], 'characters');
 * // result.isWithinAll = false (over Twitter's 280 limit)
 * // result.overLimits = [{ platform: 'twitter', limit: 280, overBy: 20 }]
 *
 * @deprecated Use checkLimitsWithCounting for character limits to handle emojis correctly
 */
export function checkLimits(value: number, platforms: string[], limitType: LimitType): LimitCheckResult {
  const normalizedPlatforms = platforms.map((p) => p.toLowerCase());

  // Use default platform if none selected
  const effectivePlatforms = normalizedPlatforms.length > 0 ? normalizedPlatforms : ['linkedin'];

  const withinLimits: LimitCheckResult['withinLimits'] = [];
  const overLimits: LimitCheckResult['overLimits'] = [];

  for (const platform of effectivePlatforms) {
    const limit = getPlatformLimit(platform, limitType);
    const displayName = getPlatformDisplayName(platform);

    if (value <= limit) {
      withinLimits.push({ platform, limit, displayName });
    } else {
      overLimits.push({ platform, limit, displayName, overBy: value - limit });
    }
  }

  const mostRestrictivePlatform = getMostRestrictivePlatform(effectivePlatforms, limitType);
  const mostRestrictiveLimit = getPlatformLimit(mostRestrictivePlatform, limitType);

  return {
    isWithinAll: overLimits.length === 0,
    withinLimits,
    overLimits,
    mostRestrictiveLimit,
    mostRestrictivePlatform,
  };
}

/**
 * Check text against all selected platform limits with proper character counting
 *
 * Uses platform-specific character counting rules:
 * - Twitter/X: Emojis count as 2 characters
 * - Other platforms: Standard grapheme counting (1 emoji = 1 character)
 *
 * @param text - The text content to check
 * @param platforms - Array of platform keys
 * @param limitType - Type of limit to check (only 'characters' uses special counting)
 * @returns Detailed check result with per-platform breakdown
 *
 * @example
 * const result = checkLimitsWithCounting("Hello face", ['linkedin', 'twitter'], 'characters');
 * // Twitter counts emoji as 2, so "Hello face" = 8 characters for Twitter
 * // LinkedIn counts emoji as 1, so "Hello face" = 7 characters for LinkedIn
 */
export function checkLimitsWithCounting(
  text: string,
  platforms: string[],
  limitType: LimitType
): LimitCheckResult {
  return timeSync('checkLimitsWithCounting', () => {
    // For non-character limits, fall back to regular check with a simple value
    if (limitType !== 'characters') {
      // This shouldn't be called for non-character limits, but handle gracefully
      return checkLimits(0, platforms, limitType);
    }

    const normalizedPlatforms = platforms.map((p) => p.toLowerCase());
    const effectivePlatforms = normalizedPlatforms.length > 0 ? normalizedPlatforms : ['linkedin'];

    const withinLimits: LimitCheckResult['withinLimits'] = [];
    const overLimits: LimitCheckResult['overLimits'] = [];

    // Track which platform has the highest effective character count
    // (for finding the most restrictive when considering emoji weighting)
    let mostRestrictivePlatform = effectivePlatforms[0];
    let lowestRemainingChars = Infinity;

    for (const platform of effectivePlatforms) {
      const limit = getPlatformLimit(platform, limitType);
      const displayName = getPlatformDisplayName(platform);
      // Use platform-specific character counting
      const platformCharCount = countCharactersForPlatform(text, platform);
      const remainingChars = limit - platformCharCount;

      // Track which platform has the least remaining characters
      if (remainingChars < lowestRemainingChars) {
        lowestRemainingChars = remainingChars;
        mostRestrictivePlatform = platform;
      }

      if (platformCharCount <= limit) {
        withinLimits.push({ platform, limit, displayName });
      } else {
        overLimits.push({
          platform,
          limit,
          displayName,
          overBy: platformCharCount - limit,
        });
      }
    }

    const mostRestrictiveLimit = getPlatformLimit(mostRestrictivePlatform, limitType);

    return {
      isWithinAll: overLimits.length === 0,
      withinLimits,
      overLimits,
      mostRestrictiveLimit,
      mostRestrictivePlatform,
    };
  });
}

/**
 * Calculate the percentage of limit used
 *
 * @param value - Current value
 * @param limit - Maximum limit
 * @returns Percentage (0-100+)
 */
export function calculatePercentage(value: number, limit: number): number {
  if (limit === 0) return 0;
  return (value / limit) * 100;
}

/**
 * Get color class based on percentage used
 *
 * @param percentage - Percentage of limit used (0-100+)
 * @returns Tailwind color class
 */
export function getPercentageColorClass(percentage: number): string {
  if (percentage >= 100) {
    return 'text-danger'; // Red - over limit
  }
  if (percentage >= 95) {
    return 'text-danger'; // Red - almost at limit
  }
  if (percentage >= 80) {
    return 'text-warning'; // Yellow - approaching limit
  }
  return 'text-success'; // Green - safe
}

/**
 * Get background color class for progress bars
 *
 * @param percentage - Percentage of limit used
 * @returns Tailwind background color class
 */
export function getProgressBarColorClass(percentage: number): string {
  if (percentage >= 100) {
    return 'bg-danger';
  }
  if (percentage >= 95) {
    return 'bg-danger';
  }
  if (percentage >= 80) {
    return 'bg-warning';
  }
  return 'bg-success';
}

/**
 * Format a number for display (with commas)
 *
 * @param value - Number to format
 * @returns Formatted string
 */
export function formatNumber(value: number): string {
  return value.toLocaleString();
}

/**
 * Format video duration from seconds
 *
 * @param seconds - Duration in seconds
 * @returns Formatted string (e.g., "10:00" or "1:00:00")
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get all platform keys
 */
export function getAllPlatforms(): string[] {
  return Object.keys(PLATFORM_LIMITS);
}

/**
 * Check if a platform is supported
 */
export function isPlatformSupported(platform: string): boolean {
  return platform.toLowerCase() in PLATFORM_LIMITS;
}

export default PLATFORM_LIMITS;
